import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { moment, formatISODate, getDatesDifferences } from 'component-utilities/dates/dates';

import styles from './Calendar.scss';

export function formatDate(date) {
    return date.format('MMMM YYYY');
}

export default function CalendarGenerator(type) {
    return class CalendarDatePicker extends Component {
        static propTypes = {
            selectedDate: PropTypes.string,
            onDateSelected: PropTypes.func,
            minDate: PropTypes.string,
            maxDate: PropTypes.string,
        };

        static defaultProps = {
            selectedDate: null,
            onDateSelected: () => {},
            minDate: null,
            maxDate: null,
        };

        static getDerivedStateFromProps(nextProps, prevState) {
            if (nextProps.selectedDate !== prevState.cachedSelectedDate && nextProps.selectedDate) {
                return {
                    actualDate: moment.utc(nextProps.selectedDate),
                    cachedSelectedDate: nextProps.selectedDate,
                };
            }

            return null;
        }

        constructor(props) {
            super(props);

            const actualDate = props.selectedDate ? moment.utc(props.selectedDate) : moment();

            this.state = { actualDate };

            this.validateInitialSelectedDate();
        }

        componentDidUpdate(prevProps) {
            const { maxDate, minDate } = this.props;

            // If maxDate or mindDate values changed we need to update the initially rendered
            //  calendar on UI
            if (
                (maxDate && maxDate !== prevProps.maxDate) ||
        (minDate && minDate !== prevProps.minDate)
            ) {
                this.updateActualDate(this.props);
            }
        }

        onActualDateChange(nextDate) {
            return () => {
                this.setState({ actualDate: nextDate });
            };
        }

        onDateSelected(day) {
            return () => {
                this.props.onDateSelected(formatISODate(moment(this.dates.actualDate).date(day)));
            };
        }

        getActiveDayClass(day) {
            const { minDate, maxDate } = this.props;
            const actualDay = moment(this.dates.actualDate).date(day);

            if (
                (minDate && getDatesDifferences(minDate, actualDay) > 0) ||
        (maxDate && getDatesDifferences(maxDate, actualDay) < 0)
            ) {
                return 'day-disabled';
            }

            switch (true) {
            case formatISODate(actualDay) === formatISODate(moment(this.props.selectedDate)):
                return 'day-selected';
            case formatISODate(actualDay) === formatISODate(moment()):
                return 'day-today-active';
            default:
                return 'day-normal';
            }
        }

        get dates() {
            const { actualDate } = this.state;

            return {
                actualDate,
                nextMonth: moment(actualDate).add(1, 'months'),
                nextYear: moment(actualDate).add(1, 'years'),
                prevMonth: moment(actualDate).subtract(1, 'months'),
                prevYear: moment(actualDate).subtract(1, 'years'),
            };
        }

        get days() {
            const { actualDate, prevMonth } = this.dates;

            const daysInActualMonth = actualDate.daysInMonth();
            const daysInPrevMonth = prevMonth.daysInMonth();
            const firstWeekDayInActualMonth = actualDate.startOf('month').weekday() || 7;

            // We always show 6 weeks of days on the UI.
            return {
                prevMonth: [...Array(daysInPrevMonth).keys()].slice(-firstWeekDayInActualMonth),
                actualMonth: [...Array(daysInActualMonth).keys()],
                nextMonth: [...Array(42 - firstWeekDayInActualMonth - daysInActualMonth).keys()],
            };
        }

        validateInitialSelectedDate() {
            const { minDate, maxDate, selectedDate } = this.props;

            if (selectedDate) {
                if (minDate && moment(minDate).diff(selectedDate) > 0) {
                    this.props.onDateSelected(formatISODate(moment(minDate)));
                } else if (maxDate && moment(maxDate).diff(selectedDate) < 0) {
                    this.props.onDateSelected(formatISODate(moment(maxDate)));
                }
            }
        }

        updateActualDate({ minDate, maxDate }) {
            const { actualDate } = this.state;

            if (minDate && moment(minDate).diff(actualDate) > 0) {
                this.setState({ actualDate: moment(minDate) });
            } else if (maxDate && moment(maxDate).diff(actualDate) < 0) {
                this.setState({ actualDate: moment(maxDate) });
            }
        }

        render() {
            const { actualDate, nextMonth, nextYear, prevMonth, prevYear } = this.dates;

            return (
                <span className={styles[type]}>
                    <span className={styles['calendar-header']}>
                        <span
                            className={styles['nav-year-back']}
                            role="presentation"
                            onClick={this.onActualDateChange(prevYear)}
                            title={formatDate(prevYear)}
                        >
                            <span className={styles['arrow-left']} />
                        </span>
                        <span
                            className={styles['nav-month-back']}
                            role="presentation"
                            onClick={this.onActualDateChange(prevMonth)}
                            title={formatDate(prevMonth)}
                        >
                            <span className={styles['arrow-left']} />
                        </span>
                        <span className={styles['month-name']}>
                            {formatDate(actualDate)}
                        </span>
                        <span
                            className={styles['nav-year-next']}
                            role="presentation"
                            onClick={this.onActualDateChange(nextYear)}
                            title={formatDate(nextMonth)}
                        >
                            <span className={styles['arrow-right']} />
                        </span>
                        <span
                            className={styles['nav-month-next']}
                            role="presentation"
                            onClick={this.onActualDateChange(nextMonth)}
                            title={formatDate(nextYear)}
                        >
                            <span className={styles['arrow-right']} />
                        </span>
                    </span>
                    <span className={styles['calendar-days-of-week']}>
                        {[...Array(7).keys()].map(key => (
                            <span key={key} className={styles['day-of-week']}>
                                {moment()
                                    .weekday(key)
                                    .format('ddd')}
                            </span>
                        ))}
                    </span>
                    <span className={styles['calendar-dates']}>
                        {this.days.prevMonth.map(day => (
                            <span key={day} className={styles['day-disabled']}>
                                {day + 1}
                            </span>
                        ))}
                        {this.days.actualMonth.map(day => (
                            <span
                                key={day}
                                role="presentation"
                                onClick={this.onDateSelected(day + 1)}
                                className={styles[this.getActiveDayClass(day + 1)]}
                            >
                                {day + 1}
                            </span>
                        ))}
                        {this.days.nextMonth.map(day => (
                            <span key={day} className={styles['day-disabled']}>
                                {day + 1}
                            </span>
                        ))}
                    </span>
                </span>
            );
        }
    };
}
